import * as React from "react";
import { useMappedState } from "redux-react-hook";
import { useCombobox } from "downshift";
import styled, { css } from "styled-components";

import {
  IDrop,
  useDropsApi
} from "src/core/drops/api/drops.api";
import type { IStore } from "src/core/shared/store";
import {
  FilterParams,
  PaginatedParams
} from "src/library/utils";
import { Icon } from "src/library/components/Icon";
import { Text } from "src/library/components/Text";
import { LoadingSpinner } from "src/library/components/LoadingSpinner";
import { Icons } from "src/library/components/Icon/types";
import { useCurrentUser } from "src/core/users/hooks";

import { DropSearchComponent } from "./DropSearchComponent";
import { CustomerDropStatuses } from "../../customers/api/customers.api";

interface UserOrderableDropSelect {
  onDropsChange: (data: IDrop | null) => void;
  selectedDrop?: IDrop | null;
  dropPagination?: PaginatedParams<IDrop>,
  debounceTime?: number;
  disabled?: boolean;
  orderVariantId?: number | null;
  disableRemoveDrop?: boolean;
  preFilters?: FilterParams<IDrop>;
}

const defaultPagination: PaginatedParams<IDrop> = {
  page: 0,
  limit: 100,
  offset: 0,
  sort: [ [ "updatedAt", "DESC" ] ]
};

export const UserOrderableDropSelect: React.ReactFCWithChildren<UserOrderableDropSelect> = ({
  onDropsChange, dropPagination, orderVariantId, selectedDrop, disabled, disableRemoveDrop, preFilters

}) => {
  const { terminology } = useMappedState(({ global }: IStore) => ({ terminology: global.settings && global.settings.terminology }));
  const [ dropsResponse, getDrops ] = useDropsApi("LIST_USER_ORDERABLE_DROPS");
  const [ userDropsResponse, getUserDrops ] = useDropsApi("GET_USER_DROPS");
  const [ detailedDrop, getDetailedDrop ] = useDropsApi("GET_DROP");
  const [ showSearch, setShowSearch ] = React.useState(false);
  const [ isLoading, setIsLoading ] = React.useState(false);
  const [ { currentUser } ] = useCurrentUser();

  React.useEffect(() => {
    if (detailedDrop.data) {
      onDropsChange(detailedDrop.data);
    }
  }, [ detailedDrop.data, onDropsChange ]);

  const dropFilters: FilterParams<IDrop> = React.useMemo(() => {
    return {
      ...preFilters,
      status: CustomerDropStatuses.Active
    };
  }, [ preFilters ]);

  // On mount, get the drops
  React.useEffect(() => {
    if (orderVariantId) {
      getDrops({
        orderVariantId,
        pagination: dropPagination || defaultPagination,
        filters: dropFilters
      });
    } else if (currentUser?.id) {
      getUserDrops({
        userId: currentUser.id,
        pagination: dropPagination || defaultPagination,
        filters: dropFilters
      });
    }
  }, [
    getDrops,
    dropFilters,
    dropPagination,
    orderVariantId,
    currentUser?.id,
    getUserDrops
  ]);

  // update local loading state
  React.useEffect(() => {
    if (selectedDrop || selectedDrop === null) {
      setIsLoading(false);
    }
  }, [ selectedDrop ]);

  const results = React.useMemo(() => {
    return dropsResponse?.data?.result || userDropsResponse.data?.result || [];
  }, [ dropsResponse?.data?.result, userDropsResponse.data?.result ]);

  // handle selecting a drop
  const dropDownShiftProps = useCombobox<IDrop>({
    items: results,
    itemToString: () => "",
    onSelectedItemChange: changes => {
      if (changes.selectedItem) {
        getDetailedDrop(changes.selectedItem.id);
        setShowSearch(false);

        return;
      }
    }
  });

  if (showSearch) {
    return (
      <DropSearchComponent
        error={dropsResponse.error || userDropsResponse.error}
        terminology={terminology ?? undefined}
        onSearch={(data: string) => {
          const filterData: FilterParams<IDrop> = { $or: [ { name: { $like: `%${data}%` } }, { branchNumber: { $like: `%${data}%` } } ] };

          if (orderVariantId) {
            getDrops({
              orderVariantId,
              pagination: {
                ...defaultPagination,
                limit: 50
              },
              filters: {
                ...filterData,
                ...dropFilters
              }
            });
          } else if (currentUser?.id) {
            getUserDrops({
              userId: currentUser.id,
              pagination: {
                ...defaultPagination,
                limit: 50
              },
              filters: {
                ...filterData,
                ...dropFilters
              }
            });
          }
        }}
        loading={dropsResponse.pending || userDropsResponse.pending}
        searchResults={results}
        downshiftProps={dropDownShiftProps}
        placeholder={`Search ${terminology?.drop.plural.text || "drops"}...`}
        onClose={() => setShowSearch(false)}
        autoFocus={showSearch}
      />
    );
  }

  return (
    <SelectedDropWrapper
      hasDrop={!!selectedDrop}
      disabled={!!disabled}
    >
      {!isLoading && (
        <div
          className="selected-drop"
          onClick={() => {
            if (!disabled) {
              setShowSearch(!showSearch);
            }
          }}
        >
          {!selectedDrop && (
            <Icon
              className="drop-icon"
              icon={Icons.BRANCH}
              $fillColor="midGrey"
            />
          )}

          <Text>
            {selectedDrop ? `(${selectedDrop.branchNumber}) ${selectedDrop.name}` : " - "}
          </Text>
        </div>
      )}

      {isLoading && (
        <div className="loading-container">
          <LoadingSpinner
            type="flat"
            position="inline"
          />
        </div>
      )}

      {selectedDrop && !disabled && !disableRemoveDrop && (
        <div className="remove-drop">
          <Icon
            icon={Icons.CLOSE}
            $fillColor="primaryDark"
            onClick={() => {
              onDropsChange(null);
            }}
          />
        </div>
      )}
    </SelectedDropWrapper>
  );
};

const SelectedDropWrapper = styled.div<{hasDrop: boolean, disabled: boolean}>`
  ${({
    theme, hasDrop, disabled
  }) => css`
    max-width: 400px;
    display: flex;

    .loading-container {
      min-width: 160px;
      display:flex;
      align-items: center;
    }

    .selected-drop {
      color: black;
      background-color: ${theme.colors.white};
      border: 1px solid ${theme.colors.lightGrey};
      height: 40px;
      border-radius: ${theme.borderRadius};
      display: flex;
      align-items: center;
      padding: ${theme.spacing.padding.sm} ${theme.spacing.padding.md};
      justify-content: ${hasDrop ? "space-between" : "space-start"};
      font-weight: bold;
      width: 100%;
  
      &:hover {
        border-color: ${theme.colors.midGrey};
        cursor: ${disabled ? "not-allowed" : "pointer"};
      }

    }
    
    .drop-icon {
      margin-right: ${theme.spacing.margins.md};
    }

    .remove-drop {
      margin-left: ${theme.spacing.margins.sm};
      display: flex;
      justify-content: center;
      align-items: center;
      cursor: pointer;
    }
  `}
`;

export default UserOrderableDropSelect;
